import { Root as PortalRoot } from '@radix-ui/react-portal'
import { ToastProvider as ToastProviderPrimitive } from '@radix-ui/react-toast'
import { ReactNode, createContext, useCallback, useContext, useState } from 'react'

import { Toast, ToastProps } from '../components/Toast'

import { Viewport as ToastViewport } from '../styles/toast.style'

interface ToastManager {
  toasts: ToastProps[]
  addToast: (toast: ToastProps) => void
  updateToast: (id: string, patch: Partial<ToastProps>) => void
}

export const ToastContext = createContext<ToastManager>({
  toasts: [],
  addToast: (_: ToastProps) => {},
  updateToast: (_id: string, _patch: Partial<ToastProps>) => {},
})

/* To call a toast:
    const { addToast } = useToast()
    addToast({ variant: 'info', title: 'File successfully deleted' })

   For loading → success pattern:
    addToast({ id: 'booking', variant: 'loading', title: 'Creating lessons...' })
    updateToast('booking', { variant: 'success', title: 'Lessons booked!' }) */
export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([])
  const addToast = useCallback((toast: ToastProps) => setToasts((prev) => [...prev, toast]), [])

  // Replace the toast with a new instance so Radix picks up the new duration
  const updateToast = useCallback((id: string, patch: Partial<ToastProps>) => {
    setToasts((prev) => {
      const idx = prev.findIndex((t) => t.id === id)
      if (idx === -1) return prev
      const updated = { ...prev[idx], ...patch, id: `${id}-done` }
      return [...prev.slice(0, idx), ...prev.slice(idx + 1), updated]
    })
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, updateToast }}>
      <ToastProviderPrimitive>
        {children}
        {toasts.map((toast: ToastProps, key: number) => (
          <Toast {...toast} key={toast.id ?? key} />
        ))}
        <PortalRoot>
          <ToastViewport />
        </PortalRoot>
      </ToastProviderPrimitive>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
